import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import attendanceService from '../../../services/attendanceService';
import geoFenceService from '../../../services/geoFenceService';
import employeeService from '../../../services/employeeService';
import fingerprintService from '../../../services/fingerprintService';
import NotificationToast from '../../../components/ui/NotificationToast';
import FingerprintEnrollmentModal from './FingerprintEnrollmentModal';
import MonthlyAttendanceEntry from './MonthlyAttendanceEntry';

const AttendanceTracking = ({ userProfile, selectedEmployeeForAttendance, clientId }) => {
  // Check if user has permission to enroll fingerprints (Super Admin or HR Admin only)
  const canEnrollFingerprint = userProfile?.role === 'super_admin' || 
                                userProfile?.role === 'hr_admin' || 
                                userProfile?.role === 'admin';
  
  console.log('[AttendanceTracking] User role:', userProfile?.role, 'Can enroll:', canEnrollFingerprint);
  const [todayStatus, setTodayStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [attendanceMethod] = useState('manual'); // 'manual' only
  const [manualEntryMode, setManualEntryMode] = useState('daily'); // 'daily' or 'monthly'
  const [manualTime, setManualTime] = useState({
    clock_in: '',
    clock_out: ''
  });
  const [fingerprintStatus, setFingerprintStatus] = useState(null);
  const [fingerprintEnrolled, setFingerprintEnrolled] = useState(null); // null = not checked, true = enrolled, false = not enrolled
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Update employee when selectedEmployeeForAttendance changes
  useEffect(() => {
    if (selectedEmployeeForAttendance) {
      console.log('[AttendanceTracking] ========================================');
      console.log('[AttendanceTracking] 🎯 EMPLOYEE SELECTED FROM TABLE');
      console.log('[AttendanceTracking] Employee object:', selectedEmployeeForAttendance);
      console.log('[AttendanceTracking] Employee ID:', selectedEmployeeForAttendance.id);
      console.log('[AttendanceTracking] Employee name:', selectedEmployeeForAttendance.full_name || selectedEmployeeForAttendance.name);
      console.log('[AttendanceTracking] ========================================');
      
      // Set employee immediately
      setEmployee(selectedEmployeeForAttendance);
      console.log('[AttendanceTracking] ✅ Employee state set');
      
      // Reset enrollment status while checking
      setFingerprintEnrolled(null);
      console.log('[AttendanceTracking] 🔄 Enrollment status reset to null (checking...)');
      
      // Load today's status for the selected employee
      attendanceService.getTodayStatus(selectedEmployeeForAttendance.id)
        .then(result => {
          if (result?.success) {
            setTodayStatus(result.data);
          }
        })
        .catch(error => {
          console.log('Error loading today status for selected employee:', error);
        });
      
      // Always check enrollment for selected employee immediately
      const checkEnrollmentForSelected = async () => {
        try {
          console.log('[AttendanceTracking] ✅ Starting enrollment check for selected employee:', selectedEmployeeForAttendance.id);
          console.log('[AttendanceTracking] Employee details:', {
            id: selectedEmployeeForAttendance.id,
            name: selectedEmployeeForAttendance.full_name || selectedEmployeeForAttendance.name,
            email: selectedEmployeeForAttendance.email
          });
          
          // Set a timeout to ensure we don't stay in loading state forever
          const timeoutId = setTimeout(() => {
            console.log('[AttendanceTracking] ⚠️ Enrollment check timeout, defaulting to not enrolled');
            setFingerprintEnrolled(false);
          }, 5000); // 5 second timeout
          
          try {
            // Check device status (non-blocking, but we'll use it for UI)
            fingerprintService.getDeviceStatus()
              .then(deviceStatus => {
                console.log('[AttendanceTracking] Device status updated:', deviceStatus);
                setFingerprintStatus(deviceStatus);
              })
              .catch(err => {
                console.error('[AttendanceTracking] Device status check error:', err);
              });
            
            // Always try to check enrollment, even if device is not available
            // This allows us to show enrollment status from database
            console.log('[AttendanceTracking] 📞 Calling checkEnrollment API for employee ID:', selectedEmployeeForAttendance.id);
            const enrollmentResult = await fingerprintService.checkEnrollment(selectedEmployeeForAttendance.id);
            clearTimeout(timeoutId);
            console.log('[AttendanceTracking] ✅ Enrollment API call completed');
            
            console.log('[AttendanceTracking] Enrollment result:', enrollmentResult);
            console.log('[AttendanceTracking] Enrollment result type:', typeof enrollmentResult);
            console.log('[AttendanceTracking] Enrollment result keys:', enrollmentResult ? Object.keys(enrollmentResult) : 'null');
            
            // Handle different response formats
            // apiClient.get wraps the response, so we need to check enrollmentResult.data.data.enrolled
            let isEnrolled = false;
            
            if (enrollmentResult) {
              console.log('[AttendanceTracking] Full enrollment result:', JSON.stringify(enrollmentResult, null, 2));
              
              // Check if success is explicitly false
              if (enrollmentResult.success === false) {
                console.log('[AttendanceTracking] Enrollment check returned success: false');
                isEnrolled = false;
              } else if (enrollmentResult.success === true || enrollmentResult.success === undefined) {
                // apiClient.get wraps response, so check enrollmentResult.data.data.enrolled
                // or enrollmentResult.data.enrolled (depending on how backend returns it)
                if (enrollmentResult.data?.enrolled !== undefined) {
                  isEnrolled = enrollmentResult.data.enrolled === true;
                  console.log('[AttendanceTracking] Found enrolled in data.enrolled:', isEnrolled);
                } else if (enrollmentResult.data?.data?.enrolled !== undefined) {
                  isEnrolled = enrollmentResult.data.data.enrolled === true;
                  console.log('[AttendanceTracking] Found enrolled in data.data.enrolled:', isEnrolled);
                } else if (enrollmentResult.enrolled !== undefined) {
                  isEnrolled = enrollmentResult.enrolled === true;
                  console.log('[AttendanceTracking] Found enrolled in root:', isEnrolled);
                } else {
                  // No enrolled field found, default to not enrolled
                  console.warn('[AttendanceTracking] No enrolled field found in response, defaulting to not enrolled');
                  console.warn('[AttendanceTracking] Available keys:', Object.keys(enrollmentResult));
                  if (enrollmentResult.data) {
                    console.warn('[AttendanceTracking] Data keys:', Object.keys(enrollmentResult.data));
                    if (enrollmentResult.data.data) {
                      console.warn('[AttendanceTracking] Data.data keys:', Object.keys(enrollmentResult.data.data));
                    }
                  }
                  isEnrolled = false;
                }
              } else {
                // Unknown success value
                console.warn('[AttendanceTracking] Unknown success value:', enrollmentResult.success);
                isEnrolled = false;
              }
            } else {
              console.warn('[AttendanceTracking] No enrollment result returned, defaulting to not enrolled');
              isEnrolled = false;
            }
            
            setFingerprintEnrolled(isEnrolled);
            console.log('[AttendanceTracking] ✅ Employee enrolled status set to:', isEnrolled);
            
          } catch (enrollmentError) {
            clearTimeout(timeoutId);
            // If enrollment check fails, assume not enrolled
            console.error('[AttendanceTracking] ❌ Enrollment check error:', enrollmentError);
            console.error('[AttendanceTracking] Error details:', {
              message: enrollmentError.message,
              stack: enrollmentError.stack
            });
            setFingerprintEnrolled(false);
          }
        } catch (error) {
          console.error('[AttendanceTracking] ❌ Error checking fingerprint status for selected employee:', error);
          setFingerprintEnrolled(false);
        }
      };
      
      // Run enrollment check immediately
      checkEnrollmentForSelected();
    } else {
      // Fall back to logged-in user's employee
      loadTodayStatus();
    }
  }, [selectedEmployeeForAttendance]);

  useEffect(() => {
    // Only get location if using geo-fencing method
    if (attendanceMethod === 'geo_fencing') {
      getCurrentLocation();
    }
    // Check fingerprint device status when fingerprint method is selected
    if (attendanceMethod === 'fingerprint') {
      // Use selectedEmployeeForAttendance directly if available, otherwise use employee state
      const employeeToCheck = selectedEmployeeForAttendance || employee;
      if (employeeToCheck) {
        console.log('[AttendanceTracking] Triggering fingerprint status check for employee:', employeeToCheck.id);
        checkFingerprintStatus();
      } else {
        console.log('[AttendanceTracking] Fingerprint method selected but no employee available');
      }
    }
    return () => {
      stopCamera();
    };
  }, [attendanceMethod, employee, selectedEmployeeForAttendance]);

  const loadTodayStatus = async () => {
    try {
      // Only try to load employee if we don't have one already and we have a selected employee
      if (employee || selectedEmployeeForAttendance) {
        const emp = employee || selectedEmployeeForAttendance;
        const result = await attendanceService.getTodayStatus(emp.id);
        if (result?.success) {
          setTodayStatus(result.data);
        }
        return;
      }

      // Try to find employee by email only if no employee is selected
      // This is mainly for employees viewing their own attendance
      if (userProfile?.email) {
        const empResult = await employeeService.getEmployees({ search: userProfile.email });
        if (empResult?.success && empResult.data?.length > 0) {
          const emp = empResult.data[0];
          setEmployee(emp);
          
          const result = await attendanceService.getTodayStatus(emp.id);
          if (result?.success) {
            setTodayStatus(result.data);
          }
        } else {
          // Employee not found - this is normal for non-employee users (like telecallers, admins)
          // Only log if in development mode
          if (import.meta.env.DEV) {
            console.log('Employee not found for email:', userProfile.email, '- This is normal for non-employee users');
          }
          setEmployee(null);
        }
      }
    } catch (error) {
      console.log('Error loading today status:', error);
      setEmployee(null);
    }
  };

  const isSecureContext = () => {
    // Check if we're in a secure context (HTTPS or localhost)
    if (window.isSecureContext) {
      return true;
    }
    
    // Check protocol
    if (window.location.protocol === 'https:') {
      return true;
    }
    
    // Check if localhost or 127.0.0.1 (only these IP addresses work with HTTP)
    const hostname = window.location.hostname.toLowerCase();
    return hostname === 'localhost' || hostname === '127.0.0.1';
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser. Please use a modern browser with geolocation support.');
      return;
    }

    // Check for secure context
    if (!isSecureContext()) {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      
      let errorMessage = 'Geolocation requires HTTPS or localhost.\n\n';
      
      if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        errorMessage += `You are accessing via: ${protocol}//${hostname}:${window.location.port}\n\n`;
        errorMessage += 'Solutions:\n';
        errorMessage += '1. Use localhost: http://localhost:4028\n';
        errorMessage += '2. Set up HTTPS (recommended for production)\n';
        errorMessage += '3. Use Manual Attendance Entry instead\n';
        errorMessage += '4. Use Finger Print Entry (if device is available)';
      } else {
        errorMessage += 'Please ensure you are using http://localhost:4028 or http://127.0.0.1:4028';
      }
      
      setLocationError(errorMessage);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setLocationError(null);
      },
      (error) => {
        let errorMessage = 'Failed to get location: ';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'User denied geolocation permission. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += error.message;
            break;
        }
        setLocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (error) {
      setNotification({ message: 'Failed to access camera: ' + error.message, type: 'error' });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Compress image
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  const handleClockIn = async () => {
    if (!employee) {
      setNotification({ message: 'Employee not found', type: 'error' });
      return;
    }

    try {
      setLoading(true);

      let photo = null;
      let latitude = null;
      let longitude = null;
      let notes = '';

      // Handle different attendance methods
      switch (attendanceMethod) {
        case 'manual':
          // Manual entry - use current time or user-specified time
          const clockInTime = manualTime.clock_in || new Date().toISOString();
          const result = await attendanceService.clockIn({
            employee_id: employee.id,
            clock_in_time: clockInTime,
            entry_method: 'manual',
            notes: 'Manual attendance entry'
          });

          if (result?.success) {
            setNotification({ message: 'Clocked in successfully (Manual Entry)', type: 'success' });
            loadTodayStatus();
          } else {
            setNotification({ message: result?.error || 'Failed to clock in', type: 'error' });
          }
          setLoading(false);
          return;

        case 'face_recognition':
          setCapturing(true);
          // Start camera
          await startCamera();
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Capture photo
          photo = capturePhoto();
          stopCamera();
          
          if (!photo) {
            setNotification({ message: 'Failed to capture photo', type: 'error' });
            setCapturing(false);
            setLoading(false);
            return;
          }

          // Get location if available (optional for face recognition)
          if (navigator.geolocation && isSecureContext()) {
            try {
              const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
              });
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
            } catch (err) {
              // Location optional for face recognition
              console.log('Location not available for face recognition entry');
            }
          }

          const faceResult = await attendanceService.clockIn({
            employee_id: employee.id,
            photo: photo,
            latitude: latitude,
            longitude: longitude,
            entry_method: 'face_recognition',
            notes: 'Face recognition entry'
          });

          if (faceResult?.success) {
            setNotification({ message: 'Clocked in successfully (Face Recognition)', type: 'success' });
            loadTodayStatus();
          } else {
            setNotification({ message: faceResult?.error || 'Failed to clock in', type: 'error' });
          }
          setCapturing(false);
          setLoading(false);
          return;

        case 'fingerprint':
          // Fingerprint entry
          if (!fingerprintEnrolled) {
            setNotification({ 
              message: 'Fingerprint not enrolled. Please enroll your fingerprint first.', 
              type: 'error' 
            });
            setLoading(false);
            return;
          }

          setCapturing(true);
          setNotification({ message: 'Please place your finger on the scanner...', type: 'info' });

          try {
            // Capture fingerprint
            const captureResult = await fingerprintService.captureFingerprint();
            
            if (!captureResult.success) {
              setNotification({ 
                message: captureResult.error || 'Failed to capture fingerprint', 
                type: 'error' 
              });
              setCapturing(false);
              setLoading(false);
              return;
            }

            // Verify fingerprint
            const verifyResult = await fingerprintService.verifyFingerprint(
              employee.id, 
              captureResult.template
            );

            if (!verifyResult.success || !verifyResult.data?.verified) {
              setNotification({ 
                message: 'Fingerprint verification failed. Please try again.', 
                type: 'error' 
              });
              setCapturing(false);
              setLoading(false);
              return;
            }

            // Clock in with fingerprint
            const fingerprintResult = await attendanceService.clockIn({
              employee_id: employee.id,
              fingerprint_template: captureResult.template,
              entry_method: 'fingerprint',
              notes: 'Fingerprint entry'
            });

            if (fingerprintResult?.success) {
              setNotification({ 
                message: `Clocked in successfully (Fingerprint) - Match Score: ${verifyResult.data.match_score}%`, 
                type: 'success' 
              });
              loadTodayStatus();
            } else {
              setNotification({ 
                message: fingerprintResult?.error || 'Failed to clock in', 
                type: 'error' 
              });
            }
          } catch (error) {
            setNotification({ 
              message: 'Error during fingerprint capture: ' + (error.message || 'Unknown error'), 
              type: 'error' 
            });
          } finally {
            setCapturing(false);
            setLoading(false);
          }
          return;

        case 'geo_fencing':
        default:
          // Geo-fencing entry (original implementation)
          if (!location) {
            setNotification({ message: 'Please enable location services', type: 'error' });
            setLoading(false);
            return;
          }

          setCapturing(true);
          // Start camera
          await startCamera();
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Capture photo
          photo = capturePhoto();
          stopCamera();
          
          if (!photo) {
            setNotification({ message: 'Failed to capture photo', type: 'error' });
            setCapturing(false);
            setLoading(false);
            return;
          }

          // Verify location
          const locationResult = await geoFenceService.verifyLocation(location.latitude, location.longitude);
          if (!locationResult?.success || !locationResult.data?.valid) {
            setNotification({ 
              message: locationResult?.data?.message || 'Location verification failed', 
              type: 'error' 
            });
            setCapturing(false);
            setLoading(false);
            return;
          }

          // Clock in
          const geoResult = await attendanceService.clockIn({
            employee_id: employee.id,
            photo: photo,
            latitude: location.latitude,
            longitude: location.longitude,
            entry_method: 'geo_fencing',
            notes: 'Geo-fencing entry'
          });

          if (geoResult?.success) {
            setNotification({ message: 'Clocked in successfully (Geo-fencing)', type: 'success' });
            loadTodayStatus();
          } else {
            setNotification({ message: geoResult?.error || 'Failed to clock in', type: 'error' });
          }
          setCapturing(false);
          setLoading(false);
          return;
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error clocking in:', error);
      setLoading(false);
      setCapturing(false);
      stopCamera();
    }
  };

  const handleClockOut = async () => {
    if (!employee) {
      setNotification({ message: 'Employee not found', type: 'error' });
      return;
    }

    try {
      setLoading(true);

      let photo = null;
      let latitude = null;
      let longitude = null;
      let notes = '';

      // Handle different attendance methods
      switch (attendanceMethod) {
        case 'manual':
          // Manual entry
          const clockOutTime = manualTime.clock_out || new Date().toISOString();
          const result = await attendanceService.clockOut({
            employee_id: employee.id,
            clock_out_time: clockOutTime,
            entry_method: 'manual',
            notes: 'Manual attendance entry'
          });

          if (result?.success) {
            setNotification({ message: 'Clocked out successfully (Manual Entry)', type: 'success' });
            loadTodayStatus();
          } else {
            setNotification({ message: result?.error || 'Failed to clock out', type: 'error' });
          }
          setLoading(false);
          return;

        case 'face_recognition':
          setCapturing(true);
          await startCamera();
          await new Promise(resolve => setTimeout(resolve, 500));
          
          photo = capturePhoto();
          stopCamera();
          
          if (!photo) {
            setNotification({ message: 'Failed to capture photo', type: 'error' });
            setCapturing(false);
            setLoading(false);
            return;
          }

          // Get location if available (optional)
          if (navigator.geolocation && isSecureContext()) {
            try {
              const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
              });
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
            } catch (err) {
              console.log('Location not available');
            }
          }

          const faceResult = await attendanceService.clockOut({
            employee_id: employee.id,
            photo: photo,
            latitude: latitude,
            longitude: longitude,
            entry_method: 'face_recognition',
            notes: 'Face recognition entry'
          });

          if (faceResult?.success) {
            setNotification({ message: 'Clocked out successfully (Face Recognition)', type: 'success' });
            loadTodayStatus();
          } else {
            setNotification({ message: faceResult?.error || 'Failed to clock out', type: 'error' });
          }
          setCapturing(false);
          setLoading(false);
          return;

        case 'fingerprint':
          // Fingerprint entry for clock out
          if (!fingerprintEnrolled) {
            setNotification({ 
              message: 'Fingerprint not enrolled. Please enroll your fingerprint first.', 
              type: 'error' 
            });
            setLoading(false);
            return;
          }

          setCapturing(true);
          setNotification({ message: 'Please place your finger on the scanner...', type: 'info' });

          try {
            // Capture fingerprint
            const captureResult = await fingerprintService.captureFingerprint();
            
            if (!captureResult.success) {
              setNotification({ 
                message: captureResult.error || 'Failed to capture fingerprint', 
                type: 'error' 
              });
              setCapturing(false);
              setLoading(false);
              return;
            }

            // Verify fingerprint
            const verifyResult = await fingerprintService.verifyFingerprint(
              employee.id, 
              captureResult.template
            );

            if (!verifyResult.success || !verifyResult.data?.verified) {
              setNotification({ 
                message: 'Fingerprint verification failed. Please try again.', 
                type: 'error' 
              });
              setCapturing(false);
              setLoading(false);
              return;
            }

            // Clock out with fingerprint
            const fingerprintResult = await attendanceService.clockOut({
              employee_id: employee.id,
              fingerprint_template: captureResult.template,
              entry_method: 'fingerprint',
              notes: 'Fingerprint entry'
            });

            if (fingerprintResult?.success) {
              setNotification({ 
                message: `Clocked out successfully (Fingerprint) - Match Score: ${verifyResult.data.match_score}%`, 
                type: 'success' 
              });
              loadTodayStatus();
            } else {
              setNotification({ 
                message: fingerprintResult?.error || 'Failed to clock out', 
                type: 'error' 
              });
            }
          } catch (error) {
            setNotification({ 
              message: 'Error during fingerprint capture: ' + (error.message || 'Unknown error'), 
              type: 'error' 
            });
          } finally {
            setCapturing(false);
            setLoading(false);
          }
          return;

        case 'geo_fencing':
        default:
          if (!location) {
            setNotification({ message: 'Please enable location services', type: 'error' });
            setLoading(false);
            return;
          }

          setCapturing(true);
          await startCamera();
          await new Promise(resolve => setTimeout(resolve, 500));
          
          photo = capturePhoto();
          stopCamera();
          
          if (!photo) {
            setNotification({ message: 'Failed to capture photo', type: 'error' });
            setCapturing(false);
            setLoading(false);
            return;
          }

          const geoResult = await attendanceService.clockOut({
            employee_id: employee.id,
            photo: photo,
            latitude: location.latitude,
            longitude: location.longitude,
            entry_method: 'geo_fencing',
            notes: 'Geo-fencing entry'
          });

          if (geoResult?.success) {
            setNotification({ message: 'Clocked out successfully (Geo-fencing)', type: 'success' });
            loadTodayStatus();
          } else {
            setNotification({ message: geoResult?.error || 'Failed to clock out', type: 'error' });
          }
          setCapturing(false);
          setLoading(false);
          return;
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error clocking out:', error);
      setLoading(false);
      setCapturing(false);
      stopCamera();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'late': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case 'absent': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'early_departure': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const checkFingerprintStatus = async () => {
    try {
      console.log('[AttendanceTracking] ========================================');
      console.log('[AttendanceTracking] 🔍 CHECKING FINGERPRINT STATUS');
      console.log('[AttendanceTracking] selectedEmployeeForAttendance:', selectedEmployeeForAttendance?.id, selectedEmployeeForAttendance?.full_name || selectedEmployeeForAttendance?.name);
      console.log('[AttendanceTracking] employee state:', employee?.id, employee?.full_name || employee?.name);
      
      // Use selectedEmployeeForAttendance if available, otherwise use employee
      const employeeToCheck = selectedEmployeeForAttendance || employee;
      console.log('[AttendanceTracking] Employee to check (final):', employeeToCheck?.id, employeeToCheck?.full_name || employeeToCheck?.name);
      
      if (!employeeToCheck) {
        console.warn('[AttendanceTracking] ⚠️ NO EMPLOYEE AVAILABLE - selectedEmployeeForAttendance and employee are both null/undefined');
        console.warn('[AttendanceTracking] This might happen if:');
        console.warn('[AttendanceTracking] 1. No employee selected from table');
        console.warn('[AttendanceTracking] 2. Logged-in user has no employee record');
        console.warn('[AttendanceTracking] 3. Employee state not yet updated');
      }
      
      // Check device status first (doesn't require employee)
      const deviceStatus = await fingerprintService.getDeviceStatus();
      console.log('[AttendanceTracking] Device status result:', deviceStatus);
      
      setFingerprintStatus(deviceStatus);
      
      // Always try to check enrollment if employee is available (even if device is not available)
      // This allows us to show enrollment status from database
      if (employeeToCheck) {
        console.log('[AttendanceTracking] Checking enrollment for employee:', employeeToCheck.id);
        try {
          const enrollmentResult = await fingerprintService.checkEnrollment(employeeToCheck.id);
          console.log('[AttendanceTracking] Enrollment check result:', enrollmentResult);
          
          if (enrollmentResult?.success !== false) {
            // Handle different response formats
            let isEnrolled = false;
            if (enrollmentResult?.data?.enrolled !== undefined) {
              isEnrolled = enrollmentResult.data.enrolled === true;
            } else if (enrollmentResult?.data?.data?.enrolled !== undefined) {
              isEnrolled = enrollmentResult.data.data.enrolled === true;
            } else if (enrollmentResult?.enrolled !== undefined) {
              isEnrolled = enrollmentResult.enrolled === true;
            }
            setFingerprintEnrolled(isEnrolled);
            console.log('[AttendanceTracking] Employee enrolled status:', isEnrolled);
          } else {
            // API call failed or returned error - assume not enrolled
            console.log('[AttendanceTracking] Enrollment check failed, assuming not enrolled');
            setFingerprintEnrolled(false);
          }
        } catch (enrollmentError) {
          // If enrollment check fails, assume not enrolled
          console.error('[AttendanceTracking] Enrollment check error:', enrollmentError);
          setFingerprintEnrolled(false);
        }
      } else {
        console.log('[AttendanceTracking] No employee available to check enrollment');
        setFingerprintEnrolled(null);
      }
      
      // Show error notification if device not available
      if (!deviceStatus?.available && deviceStatus?.error) {
        console.error('[AttendanceTracking] Fingerprint device error:', deviceStatus.error);
      }
    } catch (error) {
      console.error('[AttendanceTracking] Error checking fingerprint status:', error);
      setFingerprintStatus({
        success: false,
        available: false,
        error: error.message || 'Failed to check device status'
      });
      // Still try to check enrollment even if device check failed
      const employeeToCheck = selectedEmployeeForAttendance || employee;
      if (employeeToCheck) {
        try {
          const enrollmentResult = await fingerprintService.checkEnrollment(employeeToCheck.id);
          if (enrollmentResult?.success !== false) {
            let isEnrolled = false;
            if (enrollmentResult?.data?.enrolled !== undefined) {
              isEnrolled = enrollmentResult.data.enrolled === true;
            } else if (enrollmentResult?.data?.data?.enrolled !== undefined) {
              isEnrolled = enrollmentResult.data.data.enrolled === true;
            } else if (enrollmentResult?.enrolled !== undefined) {
              isEnrolled = enrollmentResult.enrolled === true;
            }
            setFingerprintEnrolled(isEnrolled);
          } else {
            setFingerprintEnrolled(false);
          }
        } catch (enrollmentError) {
          setFingerprintEnrolled(false);
        }
      } else {
        setFingerprintEnrolled(null);
      }
    }
  };

  const getMethodDescription = (method) => {
    switch (method) {
      case 'manual':
        return 'Enter attendance manually without location or photo verification';
      case 'face_recognition':
        return 'Use camera to capture face for attendance verification';
      case 'fingerprint':
        return 'Use Mantra MFS110 fingerprint scanner for biometric attendance';
      case 'geo_fencing':
        return 'Verify location and capture photo for attendance';
      default:
        return '';
    }
  };

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="mb-6">
        <h4 className="text-lg font-semibold text-foreground mb-2">Today's Attendance</h4>
        <p className="text-sm text-muted-foreground">Select attendance method and clock in/out</p>
      </div>

      {/* Attendance Method Selection */}
      <div className="mb-6 p-4 bg-card border border-border rounded-lg">
        <label className="block text-sm font-medium text-foreground mb-2">
          Attendance Entry Method
        </label>
        <p className="text-sm text-foreground font-medium">Manual Attendance Entry</p>
        <p className="text-xs text-muted-foreground mt-2">
          Supervisor enters attendance manually.
        </p>
      </div>

      {/* Fingerprint Device Status */}
      {attendanceMethod === 'fingerprint' && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon 
                name={fingerprintStatus?.available ? "CheckCircle" : "AlertCircle"} 
                size={20} 
                className={fingerprintStatus?.available ? "text-green-600" : "text-red-600"} 
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {fingerprintStatus?.available ? 'Fingerprint Device Ready' : 'Fingerprint Device Not Available'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {fingerprintStatus?.message || 'Checking device status...'}
                </p>
                {fingerprintStatus?.error && (
                  <p className="text-xs text-red-600 mt-1">
                    Error: {fingerprintStatus.error}
                  </p>
                )}
                {fingerprintStatus?.url && (
                  <p className="text-xs text-muted-foreground mt-1">
                    URL: {fingerprintStatus.url}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={checkFingerprintStatus}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={async () => {
                  console.log('Manual test of RD Service...');
                  const testResult = await fingerprintService.testRDServiceConnection();
                  console.log('Test result:', testResult);
                  if (testResult.success) {
                    setNotification({ 
                      message: `Connection test successful! Status: ${testResult.status}`, 
                      type: 'success' 
                    });
                  } else {
                    setNotification({ 
                      message: `Connection test failed: ${testResult.error}`, 
                      type: 'error' 
                    });
                  }
                }}
                title="Test RD Service connection"
              >
                <Icon name="TestTube" size={16} className="mr-2" />
                Test
              </Button>
            </div>
          </div>
          
          {/* Fingerprint Enrollment Section - Always show when fingerprint method is selected */}
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground">Fingerprint Enrollment</p>
                  {selectedEmployeeForAttendance && (
                    <span className="text-xs text-muted-foreground">
                      ({employee?.full_name || employee?.user_profiles?.full_name || employee?.name || 'Selected Employee'})
                    </span>
                  )}
                </div>
                {!employee ? (
                  <p className="text-xs text-muted-foreground">
                    {selectedEmployeeForAttendance 
                      ? 'Loading employee information...' 
                      : 'Select an employee from the table to view enrollment status'}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {fingerprintEnrolled 
                      ? 'Fingerprint is enrolled and ready to use' 
                      : 'Fingerprint not enrolled. Click "Enroll Now" to enroll your fingerprint.'}
                  </p>
                )}
              </div>
              {!employee ? (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                  <Icon name="RefreshCw" size={12} className="mr-1 animate-spin" />
                  Loading...
                </span>
              ) : fingerprintEnrolled === null ? (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                  <Icon name="RefreshCw" size={12} className="mr-1 animate-spin" />
                  Checking...
                </span>
              ) : fingerprintEnrolled ? (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                  <Icon name="CheckCircle" size={12} className="mr-1" />
                  Enrolled
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">
                    <Icon name="AlertCircle" size={12} className="mr-1" />
                    Not Enrolled
                  </span>
                  {canEnrollFingerprint ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEnrollmentModal(true)}
                      className="text-xs"
                      disabled={!fingerprintStatus?.available}
                    >
                      <Icon name="ScanLine" size={12} className="mr-1" />
                      Enroll Now
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">
                      Contact HR Admin to enroll
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Mode Selection */}
      {attendanceMethod === 'manual' && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4 bg-muted/30 p-1 rounded-lg w-fit">
            <Button
              variant={manualEntryMode === 'daily' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setManualEntryMode('daily')}
            >
              <Icon name="Calendar" size={14} className="mr-1" />
              Daily Entry
            </Button>
            <Button
              variant={manualEntryMode === 'monthly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setManualEntryMode('monthly')}
            >
              <Icon name="CalendarDays" size={14} className="mr-1" />
              Monthly Entry
            </Button>
          </div>

          {manualEntryMode === 'daily' ? (
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <h5 className="font-semibold text-foreground mb-3">Manual Time Entry</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Clock In Time
                  </label>
                  <Input
                    type="datetime-local"
                    value={manualTime.clock_in}
                    onChange={(e) => setManualTime(prev => ({ ...prev, clock_in: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to use current time
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Clock Out Time
                  </label>
                  <Input
                    type="datetime-local"
                    value={manualTime.clock_out}
                    onChange={(e) => setManualTime(prev => ({ ...prev, clock_out: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to use current time
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <MonthlyAttendanceEntry
              employee={employee || selectedEmployeeForAttendance}
              onSave={() => {
                loadTodayStatus();
                setNotification({ message: 'Monthly attendance saved successfully', type: 'success' });
              }}
            />
          )}
        </div>
      )}

      {/* Today's Status Card */}
      {todayStatus && (
        <div className="mb-6 p-6 bg-card border border-border rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h5 className="font-semibold text-foreground">Attendance Status</h5>
              <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString()}</p>
            </div>
            {todayStatus.record && (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(todayStatus.record.status)}`}>
                {todayStatus.record.status?.replace('_', ' ').toUpperCase()}
              </span>
            )}
          </div>
          
          {todayStatus.record ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Clock In</p>
                <p className="text-lg font-semibold text-foreground">
                  {todayStatus.record.clock_in_time 
                    ? new Date(todayStatus.record.clock_in_time).toLocaleTimeString() 
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Clock Out</p>
                <p className="text-lg font-semibold text-foreground">
                  {todayStatus.record.clock_out_time 
                    ? new Date(todayStatus.record.clock_out_time).toLocaleTimeString() 
                    : '-'}
                </p>
              </div>
              {todayStatus.record.work_hours && (
                <div>
                  <p className="text-sm text-muted-foreground">Work Hours</p>
                  <p className="text-lg font-semibold text-foreground">
                    {todayStatus.record.work_hours} hours
                  </p>
                </div>
              )}
              {todayStatus.record.total_break_time_minutes > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Break Time</p>
                  <p className="text-lg font-semibold text-foreground">
                    {Math.floor(todayStatus.record.total_break_time_minutes / 60)}h {todayStatus.record.total_break_time_minutes % 60}m
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">No attendance record for today</p>
          )}
        </div>
      )}

      {/* Location Status - Only show for geo-fencing */}
      {attendanceMethod === 'geo_fencing' && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name={location ? "MapPin" : "AlertCircle"} size={20} className={location ? "text-green-600" : "text-red-600"} />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {location ? 'Location Available' : 'Location Not Available'}
                </p>
                {location && (
                  <p className="text-xs text-muted-foreground">
                    {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </p>
                )}
                {locationError && (
                  <p className="text-xs text-red-600 mt-1">{locationError}</p>
                )}
                {!isSecureContext() && attendanceMethod === 'geo_fencing' && (
                  <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">
                      ⚠️ Geo Fencing requires HTTPS or localhost
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                      Current URL: <code className="bg-amber-100 dark:bg-amber-900 px-1.5 py-0.5 rounded text-xs">{window.location.href}</code>
                    </p>
                    <div className="text-xs text-amber-700 dark:text-amber-300">
                      <p className="font-medium mb-1">Solutions:</p>
                      <ul className="list-disc list-inside ml-2 space-y-0.5">
                        <li>Use <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">http://localhost:4028</code></li>
                        <li>Set up HTTPS for production use</li>
                        <li>Switch to <strong>Manual Attendance Entry</strong></li>
                        <li>Use <strong>Finger Print Entry</strong> (if device available)</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={getCurrentLocation}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      )}

      {/* Camera Preview */}
      {capturing && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <p className="text-sm font-medium text-foreground mb-2">Camera Preview</p>
          <div className="relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full max-w-md rounded-lg"
            />
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </div>
      )}

      {/* Break Time Controls */}
      {todayStatus?.clocked_in && !todayStatus?.clocked_out && (
        <div className="mb-4 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h5 className="font-semibold text-foreground mb-1">Break Time</h5>
              <p className="text-sm text-muted-foreground">
                {todayStatus?.on_break 
                  ? 'Currently on break' 
                  : 'Take a break from work'}
              </p>
              {todayStatus?.record?.break_start_time && !todayStatus?.record?.break_end_time && (
                <p className="text-xs text-muted-foreground mt-1">
                  Break started: {new Date(todayStatus.record.break_start_time).toLocaleTimeString()}
                </p>
              )}
              {todayStatus?.record?.total_break_time_minutes > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Total break time today: {todayStatus.record.total_break_time_minutes} minutes
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {!todayStatus?.on_break ? (
                <Button
                  onClick={async () => {
                    if (!employee) {
                      setNotification({ message: 'Employee not found', type: 'error' });
                      return;
                    }
                    try {
                      setLoading(true);
                      const result = await attendanceService.startBreak({
                        employee_id: employee.id
                      });
                      if (result?.success) {
                        setNotification({ message: 'Break started successfully', type: 'success' });
                        loadTodayStatus();
                      } else {
                        setNotification({ message: result?.error || 'Failed to start break', type: 'error' });
                      }
                    } catch (error) {
                      setNotification({ message: 'An error occurred', type: 'error' });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  variant="outline"
                  size="sm"
                >
                  <Icon name="Coffee" size={16} className="mr-2" />
                  Start Break
                </Button>
              ) : (
                <Button
                  onClick={async () => {
                    if (!employee) {
                      setNotification({ message: 'Employee not found', type: 'error' });
                      return;
                    }
                    try {
                      setLoading(true);
                      const result = await attendanceService.endBreak({
                        employee_id: employee.id
                      });
                      if (result?.success) {
                        setNotification({ message: 'Break ended successfully', type: 'success' });
                        loadTodayStatus();
                      } else {
                        setNotification({ message: result?.error || 'Failed to end break', type: 'error' });
                      }
                    } catch (error) {
                      setNotification({ message: 'An error occurred', type: 'error' });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  variant="outline"
                  size="sm"
                >
                  <Icon name="CheckCircle" size={16} className="mr-2" />
                  End Break
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          onClick={handleClockIn}
          disabled={loading || capturing || (todayStatus?.clocked_in) || 
            (attendanceMethod === 'geo_fencing' && !location) ||
            (attendanceMethod === 'fingerprint' && (!fingerprintStatus?.available || !fingerprintEnrolled))}
          className="flex-1 flex items-center justify-center gap-2"
          size="lg"
        >
          {capturing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              Capturing...
            </>
          ) : (
            <>
              <Icon name="Clock" size={20} />
              Clock In
            </>
          )}
        </Button>
        <Button
          onClick={handleClockOut}
          disabled={loading || capturing || !todayStatus?.clocked_in || todayStatus?.clocked_out || 
            (attendanceMethod === 'geo_fencing' && !location) ||
            (attendanceMethod === 'fingerprint' && (!fingerprintStatus?.available || !fingerprintEnrolled))}
          variant="outline"
          className="flex-1 flex items-center justify-center gap-2"
          size="lg"
        >
          {capturing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
              Capturing...
            </>
          ) : (
            <>
              <Icon name="LogOut" size={20} />
              Clock Out
            </>
          )}
        </Button>
      </div>

      {/* Fingerprint Enrollment Modal */}
      <FingerprintEnrollmentModal
        isOpen={showEnrollmentModal}
        onClose={() => {
          setShowEnrollmentModal(false);
          // Refresh enrollment status after enrollment
          if (employee) {
            checkFingerprintStatus();
          }
        }}
        employee={employee}
        onEnrolled={() => {
          setFingerprintEnrolled(true);
          checkFingerprintStatus();
        }}
      />
    </div>
  );
};

export default AttendanceTracking;
